import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import { useUser } from "../contexts/UserContext";
import InvoicePopup from "./InvoicePopup";
import api from "../utils/api";

// Custom pagination: uses DataTable's count/state, keeps current footer look
const CustomPagination = ({ currentPage, rowCount, rowsPerPage, onChangePage }) => {
  if (!rowCount || rowCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, rowCount);

  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-50 border-t border-gray-200">
      <div className="text-sm text-gray-600">
        Showing <span className="font-semibold">{start}-{end}</span> of <span className="font-semibold">{rowCount}</span>
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <button
          type="button"
          onClick={() => onChangePage(1)}
          disabled={currentPage <= 1}
          className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          First
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
          if (pageNum <= 3 || pageNum > totalPages - 3 || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => onChangePage(pageNum)}
                className={`px-2 py-1 text-sm border rounded ${
                  currentPage === pageNum ? "bg-red-600 text-white border-red-600" : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </button>
            );
          }
          if (pageNum === 4 && currentPage > 5) return <span key="dots-start" className="px-2">...</span>;
          if (pageNum === totalPages - 3 && currentPage < totalPages - 4) return <span key="dots-end" className="px-2">...</span>;
          return null;
        })}
        <button
          type="button"
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          Last
        </button>
      </div>
    </div>
  );
};

// Loading Spinner Component - More visible
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center py-20 min-h-[400px]">
    <div className="relative w-20 h-20">
      <div className="absolute top-0 left-0 w-full h-full border-4 border-red-200 rounded-full"></div>
      <div className="absolute top-0 left-0 w-full h-full border-4 border-red-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
    <p className="mt-6 text-lg text-gray-700 font-semibold">Loading tickets...</p>
    <p className="mt-2 text-sm text-gray-500">Please wait...</p>
  </div>
);

const TicketTable = React.memo(({ tickets, onTicketClick, isLoading = false, initialRowsPerPage = 10, rowsPerPage, onRowsPerPageChange }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [clientData, setclientData] = useState(null);
  const [clientInvoice, setclientInvoice] = useState(null);

  const loadTicketsData = useCallback(async () => {
    if (selectedTicket <= 0) return;
    try {
      const res = await api.get("/ticket/getClientDetail", {
        params: {
          selectedTicket: selectedTicket,
        },
      });
      if (res.data) {
        if (res.data.data.client_data) {
          setclientData(res.data.data.client_data);
        }
        if (res.data.data.invoice) {
          setclientInvoice(res.data.data.invoice);
        }
      }
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (selectedTicket > 0) {
      loadTicketsData();
    }
  }, [selectedTicket, loadTicketsData]);

  // Reset to page 1 when tickets change
  useEffect(() => {
    setCurrentPage(1);
  }, [tickets?.length]);

  // Reset to page 1 when rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  const perPage = rowsPerPage || initialRowsPerPage;

  const { user } = useUser();

  // Memoize formatDateWithTime to prevent recreation
  const formatDateWithTime = useCallback((dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const time = `${hours}:${minutes} ${ampm}`;
    return `${day}-${month}-${year} ${time}`;
  }, []);

  // Memoize getDateAgeClass
  const getDateAgeClass = useCallback((dateString) => {
    if (!dateString) return "";

    const created = new Date(dateString);
    const now = new Date();

    // Difference in days
    const diffTime = now - created;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    // Apply Tailwind classes
    if (diffDays > 180) {
      return "text-red-600 font-bold"; // > 6 months
    } else if (diffDays > 90) {
      return "text-red-500 font-bold"; // > 3 months
    }
    return "";
  }, []);

  const handleRowClick = useCallback((ticket) => {
    if (typeof onTicketClick === "function") {
      onTicketClick(ticket);
    }
  }, [onTicketClick]);

  const handleCollegeNameClick = useCallback((ticket, e) => {
    if (e) e.stopPropagation();
    // Old logic: only role_id == 1 opens getClientDetails popup; others get ticket details
    if (user?.role_id == 1) {
      setSelectedTicket(ticket.id);
      setIsPopupOpen(true);
    } else {
      handleRowClick(ticket);
    }
  }, [user?.role_id, handleRowClick]);


  const handleRowClicked = useCallback((row, e) => {
    const isCollege =
      e?.target?.closest?.(".college-name-cell") ||
      e?.target?.closest?.('[data-column-id="college"]');
    if (isCollege) {
      handleCollegeNameClick(row, e);
    } else {
      handleRowClick(row);
    }
  }, [handleCollegeNameClick, handleRowClick]);

  const handleClosePopup = useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  // Define columns for DataTable - more compact
  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "Ticket ID",
        selector: (row) => Number(row.id) || 0,
        sortable: true,
        width: "80px",
        compact: true,
        cell: (row) => (
          <div className="text-center">
            <div className="font-semibold text-gray-800 text-sm">{row.id}</div>
            <div
              className={`mt-0.5 inline-block px-1.5 py-0.5 rounded-full text-white text-xs ${
                row.priority === "Critical"
                  ? "bg-red-600"
                  : row.priority === "High"
                  ? "bg-orange-500"
                  : row.priority === "Medium"
                  ? "bg-blue-600"
                  : row.priority === "Low"
                  ? "bg-green-600"
                  : "bg-gray-400"
              }`}
            >
              {row.priority}
            </div>
          </div>
        ),
      },
    ];

    // Add College Name column if user is not role 2
    if (user?.role_id !== "2") {
      baseColumns.push({
        name: "College",
        id: "college",
        selector: (row) => row.collegeName || "",
        sortable: true,
        width: "130px",
        compact: true,
        cell: (row) => (
          <div
            className="flex flex-col text-center cursor-pointer college-name-cell"
            data-tag="allowRowEvents"
            onClick={(e) => handleCollegeNameClick(row, e)}
          >
            <div className="font-bold uppercase text-gray-800 truncate text-xs" data-tag="allowRowEvents">
              {row.collegeName}
            </div>
            <div className="italic text-gray-600 text-xs" data-tag="allowRowEvents">
              {row.customer_name}
            </div>
          </div>
        ),
      });
    }

    baseColumns.push(
      {
        name: "Module",
        selector: (row) => row.module || "",
        sortable: true,
        width: "110px",
        compact: true,
        cell: (row) => (
          <div className="text-center">
            <div className="font-medium text-gray-800 text-xs">{row.module}</div>
            <div
              className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs ${
                row.status === "New"
                  ? "bg-blue-100 text-black"
                  : row.status === "IN process"
                  ? "bg-green-100 text-black"
                  : row.status === "Pending verification & closure"
                  ? "bg-purple-100 text-black"
                  : row.status === "Closed"
                  ? "bg-red-400 text-white"
                  : row.status === "Reopen"
                  ? "bg-orange-200 text-black"
                  : "bg-gray-200 text-black"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  row.status === "New"
                    ? "bg-cyan-900"
                    : row.status === "IN process"
                    ? "bg-green-600"
                    : row.status === "Pending verification & closure"
                    ? "bg-purple-700"
                    : row.status === "Closed"
                    ? "bg-red-600"
                    : row.status === "Reopen"
                    ? "bg-orange-600"
                    : "bg-gray-600"
                }`}
              ></span>
              {row.status === "Pending verification & closure"
                ? "Pending"
                : row.status === "Reopen"
                ? "Reopened"
                : row.status}
            </div>
          </div>
        ),
      },
      {
        name: "Title",
        id: "title-column",
        selector: (row) => row.title || "",
        sortable: true,
        minWidth: "250px",
        compact: true,
        cell: (row) => (
          <div className="break-words text-sm text-left title-cell" title={row.title}>
            {row.title}
          </div>
        ),
        style: {
          textAlign: "left",
        },
      },
      {
        name: "Assigned To",
        selector: (row) => row.assignedTo || "",
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="whitespace-normal text-center text-xs">
            {row.assignedTo?.toUpperCase()} <br />
            <span className="text-gray-500">
              {row.AssignedTo_designation_id === "2"
                ? "(ENOVA)"
                : row.collegeName &&
                  `(${row.collegeName.toUpperCase()})`}
            </span>
          </div>
        ),
      },
      {
        name: "Reported By",
        selector: (row) => row.client || "",
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="whitespace-normal text-center text-xs">
            {row.client?.toUpperCase()} <br />
            <span className="text-gray-500">
              {row.ReportedBy_designation_id === "2"
                ? "(ENOVA)"
                : row.collegeName &&
                  `(${row.collegeName.toUpperCase()})`}
            </span>
          </div>
        ),
      },
      {
        name: "Issue Type",
        selector: (row) => row.issue_type || "",
        sortable: true,
        width: "100px",
        compact: true,
        cell: (row) => <div className="text-xs">{row.issue_type || "-"}</div>,
      },
      {
        name: "Created Date",
        id: "created",
        selector: (row) => row.created ? new Date(row.created).getTime() : 0,
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className={`text-xs ${getDateAgeClass(row.created)}`}>
            {formatDateWithTime(row.created)}
          </div>
        ),
      },
      {
        name: "Updated Date",
        selector: (row) => row.updated ? new Date(row.updated).getTime() : 0,
        sortable: true,
        width: "140px",
        compact: true,
        cell: (row) => (
          <div className="text-xs">{formatDateWithTime(row.updated)}</div>
        ),
      },
      {
        name: "Comments",
        selector: (row) => Number(row.comment_count) || 0,
        sortable: true,
        width: "80px",
        compact: true,
        cell: (row) => (
          <div className="text-center text-sm">{row.comment_count}</div>
        ),
      }
    );

    return baseColumns;
  }, [user?.role_id, formatDateWithTime, getDateAgeClass, handleCollegeNameClick]);

  // Custom styles for DataTable - neat center-aligned
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f3f4f6",
        color: "#374151",
        fontWeight: "600",
        fontSize: "0.75rem",
        borderBottom: "2px solid #d1d5db",
        minHeight: "45px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        "&:hover": {
          cursor: "pointer",
          backgroundColor: "#e5e7eb",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        fontSize: "0.75rem",
        paddingTop: "16px",
        paddingBottom: "16px",
        textAlign: "center",
        verticalAlign: "middle",
      },
    },
    rows: {
      style: {
        cursor: "pointer",
        minHeight: "70px",
        borderBottom: "1px solid #e5e7eb",
        "&:hover": {
          backgroundColor: "#f9fafb",
        },
        "&:nth-of-type(even)": {
          backgroundColor: "#fafafa",
        },
      },
    },
    table: {
      style: {
        overflowX: "auto",
      },
    },
    tableWrapper: {
      style: {
        display: "table",
        width: "100%",
      },
    },
  };

  // Hide sort icons and bottom rows per page selector using CSS
  // Also ensure all cell content is center-aligned
  const hideSortIconStyle = `
    .rdt_TableHeadRow .rdt_TableCol > div > div:last-child,
    .rdt_TableHeadRow .rdt_TableCol > div > span:last-child,
    .rdt_TableHeadRow .rdt_TableCol svg,
    .rdt_TableHeadRow .rdt_TableCol [class*="sort"] {
      display: none !important;
      visibility: hidden !important;
    }
    /* Custom pagination: use our component's layout, minimal wrapper */
    .rdt_Pagination {
      padding: 0 !important;
      margin: 0 !important;
      min-height: 0 !important;
    }

    .rdt_TableRow {
      user-select: none;
    }
    .rdt_TableBody .rdt_TableRow .rdt_TableCell > * {
      pointer-events: none;
    }
    /* Allow clicks on college name so getClientDetail runs (any depth inside cell) */
    .rdt_TableBody .rdt_TableCell .college-name-cell {
      pointer-events: auto !important;
      cursor: pointer !important;
    }

    /* Center-align all cell content except Title column */
    .rdt_TableBody .rdt_TableCell {
      text-align: center !important;
    }

    /* Title column cells - left aligned (target by class) */
    .rdt_TableBody .rdt_TableCell:has(.title-cell) {
      text-align: left !important;
    }

    .rdt_TableBody .rdt_TableCell > div {
      text-align: center !important;
      margin: 0 auto;
    }

    /* Override for title-cell specifically - left align */
    .rdt_TableBody .rdt_TableCell > div.title-cell {
      text-align: left !important;
      margin: 0 !important;
    }

    /* Center-align all table headers (including Title header) */
    .rdt_TableHeadRow .rdt_TableCol {
      text-align: center !important;
    }

    .rdt_TableHeadRow .rdt_TableCol > div {
      text-align: center !important;
      justify-content: center;
    }
  `;

  // Mobile view (keep existing mobile card view)
  const MobileView = useMemo(() => {
    if (!tickets || tickets.length === 0) {
      return (
        <div className="flex justify-center items-center h-40 text-base sm:text-lg text-gray-600">
          No tickets available at the moment
        </div>
      );
    }

    const start = (currentPage - 1) * (rowsPerPage || initialRowsPerPage);
    const end = start + (rowsPerPage || initialRowsPerPage);
    const paginatedTickets = tickets.slice(start, end);
    const totalPages = Math.ceil(tickets.length / (rowsPerPage || initialRowsPerPage));

    return (
      <>
        <div className="grid grid-cols-1 gap-2">
          {paginatedTickets.map((ticket) => (
          <div
            key={ticket.id}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition cursor-pointer"
            onClick={() => handleRowClick(ticket)}
          >
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm font-semibold text-gray-800">#{ticket.id}</div>
              <span
                className={`px-2 py-0.5 rounded-full text-white text-xs ${
                  ticket.priority === "Critical"
                    ? "bg-red-600"
                    : ticket.priority === "High"
                    ? "bg-orange-500"
                    : ticket.priority === "Medium"
                    ? "bg-blue-600"
                    : ticket.priority === "Low"
                    ? "bg-green-600"
                    : "bg-gray-400"
                }`}
              >
                {ticket.priority}
              </span>
            </div>

            {user?.role_id !== "2" && (
              <div className="text-xs text-gray-700 mb-1 truncate">
                {ticket.collegeName}
              </div>
            )}

            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-800">{ticket.module}</span>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                  ticket.status === "New"
                    ? "bg-blue-100 text-black"
                    : ticket.status === "IN process"
                    ? "bg-green-100 text-black"
                    : ticket.status === "Pending verification & closure"
                    ? "bg-purple-100 text-black"
                    : ticket.status === "Closed"
                    ? "bg-red-400 text-white"
                    : ticket.status === "Reopen"
                    ? "bg-orange-200 text-black"
                    : "bg-gray-200 text-black"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    ticket.status === "New"
                      ? "bg-cyan-900"
                      : ticket.status === "IN process"
                      ? "bg-green-600"
                      : ticket.status === "Pending verification & closure"
                      ? "bg-purple-700"
                      : ticket.status === "Closed"
                      ? "bg-red-600"
                      : ticket.status === "Reopen"
                      ? "bg-orange-600"
                      : "bg-gray-600"
                  }`}
                ></span>
                {ticket.status === "Pending verification & closure" ? "Pending" : ticket.status === "Reopen" ? "Reopened" : ticket.status}
              </span>
            </div>

            <div className="text-sm text-gray-900 mb-2 break-words">
              {ticket.title}
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <div className="font-semibold">Assigned</div>
                <div className="break-words">
                  {ticket.assignedTo.toUpperCase()} {ticket.AssignedTo_designation_id === "2" ? "(ENOVA)" : ticket.collegeName && `(${ticket.collegeName.toUpperCase()})`}
                </div>
              </div>
              <div>
                <div className="font-semibold">Reported</div>
                <div className="break-words">
                  {ticket.client.toUpperCase()} {ticket.ReportedBy_designation_id === "2" ? "(ENOVA)" : ticket.collegeName && `(${ticket.collegeName.toUpperCase()})`}
                </div>
              </div>
              <div>
                <div className="font-semibold">Created</div>
                <div className={getDateAgeClass(ticket.created)}>{formatDateWithTime(ticket.created)}</div>
              </div>
              <div>
                <div className="font-semibold">Updated</div>
                <div>{formatDateWithTime(ticket.updated)}</div>
              </div>
              <div>
                <div className="font-semibold">Comments</div>
                <div>{ticket.comment_count}</div>
              </div>
              <div>
                <div className="font-semibold">Issue</div>
                <div>{ticket.issue_type || "-"}</div>
              </div>
            </div>
          </div>
        ))}
        </div>
        {totalPages > 1 && (
          <div className="flex flex-col gap-2 px-2 sm:px-4 py-2 sm:py-3 bg-gray-50 border-t border-gray-200">
            <div className="text-xs sm:text-sm text-gray-600">
              <span className="font-semibold">{(currentPage - 1) * (rowsPerPage || initialRowsPerPage) + 1}-{Math.min(currentPage * (rowsPerPage || initialRowsPerPage), tickets.length)}</span> of <span className="font-semibold">{tickets.length}</span>
            </div>
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-0.5 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-0.5 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-0.5 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-0.5 text-xs sm:text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </>
    );
  }, [tickets, user?.role_id, handleRowClick, formatDateWithTime, getDateAgeClass, rowsPerPage, currentPage, initialRowsPerPage]);

  return (
    <div className="min-h-[calc(100vh-400px)]">
      {/* Mobile View */}
      <div className="block sm:hidden p-3">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {MobileView}
          </>
        )}
      </div>

      {/* Desktop View with DataTable */}
      <div className="hidden sm:block">
        <style>{hideSortIconStyle}</style>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto relative min-h-[400px]">
            <DataTable
              key={`${perPage}-${tickets?.length}`}
              columns={columns}
              data={tickets || []}
              onRowClicked={handleRowClicked}
              progressPending={isLoading}
              progressComponent={<LoadingSpinner />}
              persistTableHead
              customStyles={customStyles}
              highlightOnHover
              pointerOnHover
              noDataComponent={
                <div className="flex justify-center items-center h-40 text-base sm:text-lg text-gray-600 py-8">
                  No tickets available at the moment
                </div>
              }
              pagination
              paginationTotalRows={tickets?.length ?? 0}
              paginationPerPage={perPage}
              paginationDefaultPage={1}
              paginationComponent={CustomPagination}
              paginationRowsPerPageOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        </div>
      </div>

      <InvoicePopup
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        Ticket_id={selectedTicket}
        clientData={clientData}
        clientInvoice={clientInvoice}
      />
    </div>
  );
});

TicketTable.displayName = "TicketTable";

export default TicketTable;
